import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Claim } from '@wsphere/warranties/domain';
import { CLAIM_LOCK_CONFIG, ClaimLockNotice } from '../../config/claim-lock.config';
import { ClaimService } from '../../services';

export type ClaimAuthorizationDialogMode = 'authorize' | 'reverse';

interface ApiError {
  error?: { message?: string | string[]; errors?: Claim.Authorization.ReadinessIssue[] };
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-authorization-dialog',
  templateUrl: './claim-authorization.dialog.html',
  standalone: false,
})
export class ClaimAuthorizationDialog {
  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private toastSvc: ToastService,
    private claimSvc: ClaimService
  ) {}

  @ViewChild(DialogComponent) private dialog?: DialogComponent;

  @Input() claim?: Claim.Model | null;

  @Output() saved = new EventEmitter<void>();

  mode: ClaimAuthorizationDialogMode = 'authorize';
  authorization: Claim.Authorization.Model | null = null;

  saving = false;

  /** One per opening, so a retry gets the same record back. */
  private idempotencyKey = '';

  protected form = this.fb.group({
    payeeType: [null as Claim.Authorization.PayeeType | null],
    reason: [''],
    isZeroPayConfirmed: [false],
  });

  protected readonly payeeTypes = Claim.Authorization.PayeeType;

  get customerName() {
    return this.claim?.warranty?.customer?.name ?? null;
  }

  /** Paying the shop needs one assigned to the claim. */
  get shopName() {
    return this.claim?.serviceProviderId ? (this.claim.serviceProviderSnapshot?.name ?? 'Repair shop') : null;
  }

  get totals() {
    return this.mode === 'reverse' ? (this.authorization?.totals ?? null) : this.newTotals;
  }

  /** What this authorization adds, as on the server: a supplemental pays only the new jobs. */
  private get newTotals() {
    const totals = this.claim?.totals;
    if (!totals) return null;

    return Claim.Authorization.getOwnTotals(
      totals,
      Claim.Authorization.getActiveAuthorizations(this.claim?.authorizations ?? [])
    );
  }

  /** Set on open, so the warning's expanded sections don't reset. */
  lockNotice: ClaimLockNotice | null = null;

  /** What authorizing locks: the jobs not covered by an earlier authorization. */
  private getLockNotice() {
    const jobs = (this.claim?.items ?? []).filter(item => !item.deleted && !item.authorizationId).length;
    const isSupplemental = Claim.Authorization.getActiveAuthorizations(this.claim?.authorizations ?? []).length > 0;
    return CLAIM_LOCK_CONFIG.authorize.notice(jobs, isSupplemental);
  }

  get isZeroPay() {
    return this.mode === 'authorize' && this.newTotals?.netPayable === 0;
  }

  get isValid() {
    const { reason, isZeroPayConfirmed, payeeType } = this.form.getRawValue();
    if (this.mode === 'reverse') return !!reason?.trim();

    const hasPayee =
      payeeType === this.payeeTypes.CUSTOMER || (payeeType === this.payeeTypes.SERVICE_PROVIDER && !!this.shopName);
    return hasPayee && (!this.isZeroPay || !!isZeroPayConfirmed);
  }

  open(mode: ClaimAuthorizationDialogMode, authorization: Claim.Authorization.Model | null = null) {
    this.mode = mode;
    this.authorization = authorization;
    this.lockNotice = mode === 'authorize' ? this.getLockNotice() : null;
    this.idempotencyKey = crypto.randomUUID();
    this.form.reset({ payeeType: null, reason: '', isZeroPayConfirmed: false });
    this.dialog?.open();
  }

  close() {
    this.dialog?.close();
  }

  formatAmount(amount: number, currency = 'USD') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100);
  }

  async submit() {
    const claim = this.claim;
    if (!claim || !this.isValid) return;

    const { reason, isZeroPayConfirmed, payeeType } = this.form.getRawValue();

    this.saving = true;
    try {
      let reference: string | null = null;
      if (this.mode === 'authorize') {
        ({ reference } = await this.claimSvc.authorize(claim.id, {
          payeeType: payeeType ?? this.payeeTypes.CUSTOMER,
          reason: reason?.trim() || null,
          isZeroPayConfirmed: this.isZeroPay && !!isZeroPayConfirmed,
          idempotencyKey: this.idempotencyKey,
        }));
      } else if (this.authorization) {
        await this.claimSvc.reverseAuthorization(claim.id, this.authorization.id, {
          reason: reason?.trim() ?? '',
          idempotencyKey: this.idempotencyKey,
        });
      }

      this.toastSvc.show({
        type: 'success',
        title: this.mode === 'authorize' ? 'Claim authorized' : 'Authorization reversed',
        text:
          this.mode === 'authorize'
            ? `<strong>Claim #${claim.claimNumber}</strong> is authorized as <strong>${reference}</strong>. Its jobs are locked.`
            : `The jobs of <strong>Claim #${claim.claimNumber}</strong> it covered can be edited again.`,
      });

      this.close();
      this.saved.emit();
    } catch (err) {
      const error = (err as ApiError)?.error;

      const message = error?.message;
      this.toastSvc.show({
        type: 'error',
        title: this.mode === 'authorize' ? 'Unable to authorize' : 'Unable to reverse',
        text: (Array.isArray(message) ? message.join(' ') : message) || 'Something went wrong. Please try again.',
      });
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }
}
